import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BaseServiceFacade, GenericApiCall } from 'src/app/common/store/types';
import { AppState } from '../types';
import { contractActions } from './contract.actions';
import { contractSelectors } from './contract.selectors';
import {
  ContractModel,
  ContractHealthFormModel,
  ContractStatus,
  ContractStatusFlow,
  ContractStatusHistoryModel,
  CreateContractFormModel,
  HealthInsuranceContractDetailModel,
  ContractCarFormModel,
  ContractHouseFormModel,
  CarInsuranceContractModel,
  HouseInsuranceContractModel,
  ContractHealthUpdateModel,
  ContractHouseUpdateModel,
  ContractCarUpdateModel,
  CreateContractEntityModel,
  ContractAddBaseModel,
  ContractUpdateBaseModel,
  ContractRejectReasonModel,
  ContractTerminationReasonModel,
} from './types';
import { Action } from '../view/types';

@Injectable()
export class ContractService extends BaseServiceFacade {
  constructor(store: Store<AppState>) {
    super(store);
  }

  getById: GenericApiCall<ContractModel, string> = this.genericApiCall(
    contractActions.getById,
    contractSelectors.getById,
    true,
  );
  getByUniqueId: GenericApiCall<ContractModel, string> = this.genericApiCall(
    contractActions.getByUniqueId,
    contractSelectors.getByUniqueId,
    true,
  );

  getByLeadId: GenericApiCall<ContractModel, string> = this.genericApiCall(
    contractActions.getByLeadId,
    contractSelectors.getByLeadId,
    true,
  );

  getHealthDetail: GenericApiCall<
    HealthInsuranceContractDetailModel[],
    number
  > = this.genericApiCall(
    contractActions.getHealthDetail,
    contractSelectors.getHealthDetail,
    true,
  );

  getCarDetail: GenericApiCall<CarInsuranceContractModel, number> =
    this.genericApiCall(
      contractActions.getCarDetail,
      contractSelectors.getCarDetail,
      true,
    );

  getHouseDetail: GenericApiCall<HouseInsuranceContractModel, number> =
    this.genericApiCall(
      contractActions.getHouseDetail,
      contractSelectors.getHouseDetail,
      true,
    );

  getStatusFlow: GenericApiCall<ContractStatusFlow[], ContractStatus> =
    this.genericApiCall(
      contractActions.getStatusFlow,
      contractSelectors.getStatusFlow,
      true,
    );

  confirm: GenericApiCall<string, number> = this.genericApiCall(
    contractActions.confirm,
    contractSelectors.confirm,
    true,
  );

  saveAsDraft: GenericApiCall<ContractModel, number> = this.genericApiCall(
    contractActions.saveAsDraft,
    contractSelectors.saveAsDraft,
    true,
  );
  requestValidation: GenericApiCall<ContractModel, number> =
    this.genericApiCall(
      contractActions.requestValidation,
      contractSelectors.requestValidation,
      true,
    );
  validate: GenericApiCall<ContractModel, number> = this.genericApiCall(
    contractActions.validate,
    contractSelectors.validate,
    true,
  );
  reject: GenericApiCall<ContractModel, ContractRejectReasonModel> =
    this.genericApiCall(contractActions.reject, contractSelectors.reject, true);
  verify: GenericApiCall<ContractModel, number> = this.genericApiCall(
    contractActions.verify,
    contractSelectors.verify,
    true,
  );
  suspendContract: GenericApiCall<ContractModel, number> = this.genericApiCall(
    contractActions.suspendContract,
    contractSelectors.suspendContract,
    true,
  );
  closeContract: GenericApiCall<ContractModel, ContractTerminationReasonModel> =
    this.genericApiCall(
      contractActions.closeContract,
      contractSelectors.closeContract,
      true,
    );
  reopenContract: GenericApiCall<ContractModel, number> = this.genericApiCall(
    contractActions.reopenContract,
    contractSelectors.reopenContract,
    true,
  );
  getStatusActions: GenericApiCall<Action[], ContractStatus> =
    this.genericApiCall(
      contractActions.getStatusActions,
      contractSelectors.getStatusActions,
      true,
    );
  getContractModel: GenericApiCall<ContractModel, CreateContractEntityModel> =
    this.genericApiCall(
      contractActions.getContractModel,
      contractSelectors.getContractModel,
      true,
    );
  getHealthContractModel: GenericApiCall<
    HealthInsuranceContractDetailModel[],
    number
  > = this.genericApiCall(
    contractActions.getHealthContractModel,
    contractSelectors.getHealthContractModel,
    true,
  );
  getCarContractModel: GenericApiCall<CarInsuranceContractModel, number> =
    this.genericApiCall(
      contractActions.getCarContractModel,
      contractSelectors.getCarContractModel,
      true,
    );
  getHouseContractModel: GenericApiCall<HouseInsuranceContractModel, number> =
    this.genericApiCall(
      contractActions.getHouseContractModel,
      contractSelectors.getHouseContractModel,
      true,
    );
  createContract: GenericApiCall<any, ContractAddBaseModel> =
    this.genericApiCall(
      contractActions.createContract,
      contractSelectors.createContract,
      true,
    );
  createHealthContract: GenericApiCall<any, ContractHealthFormModel> =
    this.genericApiCall(
      contractActions.createHealthContract,
      contractSelectors.createHealthContract,
      true,
    );
  createCarContract: GenericApiCall<any, ContractCarFormModel> =
    this.genericApiCall(
      contractActions.createCarContract,
      contractSelectors.createCarContract,
      true,
    );
  createHouseContract: GenericApiCall<any, ContractHouseFormModel> =
    this.genericApiCall(
      contractActions.createHouseContract,
      contractSelectors.createHouseContract,
      true,
    );
  updateContract: GenericApiCall<string, ContractUpdateBaseModel> =
    this.genericApiCall(
      contractActions.updateContract,
      contractSelectors.updateContract,
      true,
    );
  updateHealthContract: GenericApiCall<string, ContractHealthUpdateModel> =
    this.genericApiCall(
      contractActions.updateHealthContract,
      contractSelectors.updateHealthContract,
      true,
    );
  updateCarContract: GenericApiCall<string, ContractCarUpdateModel> =
    this.genericApiCall(
      contractActions.updateCarContract,
      contractSelectors.updateCarContract,
      true,
    );

  updateHouseContract: GenericApiCall<string, ContractHouseUpdateModel> =
    this.genericApiCall(
      contractActions.updateHouseContract,
      contractSelectors.updateHouseContract,
      true,
    );
  getIfDataReadOnlyFromContractId: GenericApiCall<boolean, number> =
    this.genericApiCall(
      contractActions.getIfDataReadOnlyFromContractId,
      contractSelectors.getIfDataReadOnlyFromContractId,
      true,
    );

  getContractStatusHistory: GenericApiCall<
    ContractStatusHistoryModel,
    { contractId: number; contractStatusHistoryId: number }
  > = this.genericApiCall(
    contractActions.getContractStatusHistory,
    contractSelectors.getContractStatusHistory,
    true,
  );

  updateContractStatusHistory: GenericApiCall<
    ContractStatusHistoryModel,
    ContractStatusHistoryModel
  > = this.genericApiCall(
    contractActions.updateContractStatusHistory,
    contractSelectors.updateContractStatusHistory,
    true,
  );

  deleteContractStatusHistory: GenericApiCall<
    boolean,
    { contractId: number; contractStatusHistoryId: number }
  > = this.genericApiCall(
    contractActions.deleteContractStatusHistory,
    contractSelectors.deleteContractStatusHistory,
    true,
  );
}
